import React, { useLayoutEffect, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { privacyConfig as defaultPrivacyConfig } from '../config/privacyConfig';
import { loadPrivacyConfigForLocale } from '../config/configRegistry';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { generateHreflangLinks } from '../utils/seoUtils';
import { SITE_ORIGIN } from '../config/site';
import SiteHeader from './SiteHeader';
import Footer from './Footer';
import { setPrerenderReady } from '../utils/prerenderUtils';

const Privacy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, ready } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [privacyConfig, setPrivacyConfig] = useState(defaultPrivacyConfig);

  // Load localized privacy config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await loadPrivacyConfigForLocale(currentLanguage);
        setPrivacyConfig(config);
      } catch (error) {
        console.error('Failed to load privacy config:', error);
        setPrivacyConfig(defaultPrivacyConfig);
      }
    };
    loadConfig();
  }, [currentLanguage]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleBack = (e) => {
    e.preventDefault();
    navigate(-1);
  };

  const createSectionId = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const renderSection = (section) => {
    const sectionId = createSectionId(section.title);
    
    return (
      <div className="prose max-w-none">
        <h2 
          id={sectionId}
          className="text-xl font-semibold text-gray-900 mb-4 scroll-mt-20"
        >
          {section.title}
        </h2>
        {section.subsections ? (
          <div className="space-y-6 pl-4">
            {section.subsections.map((subsection) => {
              const subsectionId = createSectionId(subsection.title);
              return (
                <div 
                  key={subsectionId}
                  id={subsectionId}
                  className="border-l-2 border-gray-100 pl-4 scroll-mt-20"
                >
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {subsection.title}
                  </h3>
                  <p className="text-gray-600">
                    {subsection.content}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-600">
            {section.content}
          </p>
        )}
      </div>
    );
  };

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Set prerender ready when translations are loaded
  useEffect(() => {
    if (ready) {
      setPrerenderReady();
    }
  }, [ready]);

  // Show loading state until translations are ready
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet prioritizeSeoTags={true}>
        <html lang={currentLanguage} />
        <title>{t('legal.privacy', 'Privacy Policy')}</title>
        <meta name="description" content={t('legal.privacyDescription', "LiveLoveSpain's Privacy Policy - Learn how we protect and manage your personal information.")} />
        
        {/* Open Graph tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_ORIGIN}/privacy`} />
        <meta property="og:title" content={`${t('legal.privacy', 'Privacy Policy')} - LiveLoveSpain`} />
        <meta property="og:description" content={t('legal.privacyDescription', "LiveLoveSpain's Privacy Policy - Learn how we protect and manage your personal information.")} />
        <meta property="og:image" content={`${SITE_ORIGIN}/assets/LiveLoveSpain.webp`} />
        
        {/* Hreflang links for all supported locales */}
        {generateHreflangLinks(SITE_ORIGIN, '/privacy', currentLanguage).map((link, index) => (
          <link key={`hreflang-${link.hreflang}`} rel={link.rel} href={link.href} hreflang={link.hreflang} />
        ))}
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/privacy`} />
        <meta name="twitter:title" content={`${t('legal.privacy', 'Privacy Policy')} - LiveLoveSpain`} />
        <meta name="twitter:description" content={t('legal.privacyDescription', "LiveLoveSpain's Privacy Policy - Learn how we protect and manage your personal information.")} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/assets/LiveLoveSpain-512.png`} />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/privacy`} />
        
        {/* Breadcrumb Schema */}
        <script key="ld:page" type="application/ld+json" data-page={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/privacy`}>
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [{
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": t('common.home', 'Home'),
                  "item": `${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}`
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": t('legal.privacy', 'Privacy Policy'),
                  "item": `${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/privacy`
                }
              ]
            }]}
          )}
        </script>
      </Helmet>
      
      {/* Header */}
      <SiteHeader />

      {/* Visual breadcrumb navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/" className="hover:text-blue-600 transition-colors">
              {t('common.home')}
            </Link>
          </li>
          <li>
            <span className="mx-2">/</span>
          </li>
          <li className="text-gray-900 font-medium">
            {t('legal.privacy')}
          </li>
        </ol>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('legal.cookiesPage.back')}
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {privacyConfig.title}
            </h1>
            <p className="text-sm text-gray-500">
              {t('legal.cookiesPage.lastUpdated')} {privacyConfig.lastUpdated}
            </p>
          </div>

          <p className="text-gray-600 mb-8">
            {privacyConfig.intro}
          </p>

          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Table of Contents</h2>
            <nav>
              <ul className="space-y-2">
                {privacyConfig.sections.map((section) => {
                  const sectionId = createSectionId(section.title);
                  return (
                    <li key={sectionId}>
                      <a 
                        href={`#${sectionId}`}
                        onClick={(e) => scrollToSection(e, sectionId)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        {section.title}
                      </a>
                      {section.subsections && (
                        <ul className="pl-4 mt-2 space-y-1">
                          {section.subsections.map((subsection) => {
                            const subsectionId = createSectionId(subsection.title);
                            return (
                              <li key={subsectionId}>
                                <a 
                                  href={`#${subsectionId}`}
                                  onClick={(e) => scrollToSection(e, subsectionId)}
                                  className="text-sm text-gray-600 hover:text-blue-600"
                                >
                                  {subsection.title}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="space-y-12">
            {privacyConfig.sections.map((section) => (
              <div key={createSectionId(section.title)}>
                {renderSection(section)}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Privacy; 